import { NgTemplateOutlet } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { finalize } from 'rxjs';
import { BOOKING_CUSTOM_FIELD_TYPES } from '../../../constants/widget-constants';
import { CustomFieldOptions, CustomFieldsDao } from '../../../db-models/appointment-custom-fields-dao';
import { MappedAppointmentServicesDao } from '../../../db-models/appointment-service-dao';
import { CustomBookingMessageTemplate } from '../../../db-models/widget-conf-dao';
import { Partner } from '../../../models/global';
import { AppointmentState } from '../../../models/state.model';
import { AppointmentService } from '../../../services/appointment.service';
import { CustomEventService } from '../../../services/custom-event.service';
import { CustomersService } from '../../../services/customers.service';
import { FormsService } from '../../../services/forms.service';
import { HelperService } from '../../../services/helper.service';
import { LoggerService } from '../../../services/logger.service';
import { PartnerService } from '../../../services/partner.service';
import { UtilService } from '../../../services/util.service';
import { CustomFieldComponent } from '../custom-field/custom-field.component';

@Component({
  selector: 'app-custom-field-view',
  templateUrl: './custom-field-view.component.html',
  styleUrls: ['./custom-field-view.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  imports: [CustomFieldComponent, NgTemplateOutlet]
})
export class CustomFieldViewComponent implements OnInit {

  private formsService = inject(FormsService);
  private customEventService = inject(CustomEventService);
  private utilService = inject(UtilService);
  public partnerService = inject(PartnerService);
  private appointmentService = inject(AppointmentService);
  private customersService = inject(CustomersService);
  private helperService = inject(HelperService);

  @Input() customFields: CustomFieldsDao[];
  @Input() field: CustomFieldsDao;
  @Input() lang: string;
  @Input() selectedJsonCustomFields: {
    [key: number]: { type: string; value: any } | any;
  };
  @Input() customFieldValues: {
    [key: number]: {
      type: string,
      value: any,
      option_values: {
        custom_field_option_id: number,
        custom_field_option_value: string,
      }[]
    } | any
  };
  @Input() customFieldValuesLSNameKey: string;
  @Input() appointmentState?: AppointmentState;
  @Input() isFirst: boolean;
  @Input() isLast: boolean;
  @Input() widgetType: string;
  @Input() widgetTemplates: CustomBookingMessageTemplate[] = [];
  @Input() partner: Partner;

  protected showDependenciesGhostElement = false;
  protected selectedChildIds: number[] = [];
  protected dependenciesGhostElements = [1, 2, 3];
  protected readonly bookingCustomFieldTypes = BOOKING_CUSTOM_FIELD_TYPES;

  ngOnInit(): void {
    this.initCheckEndOfProcess();
  }

  onSelectedChildIdsChange(ids: number[]): void {
    this.selectedChildIds = ids;
  }

  onCheckboxChangeEvent(event: {
    customField: CustomFieldsDao,
    checked: boolean,
    isInit?: boolean,
  }): void {
    if (event.checked) {
      if (this.field?.child_custom_field?.id) {
        this.customFieldValues = this.removeCustomFieldValuesByIds([this.field.child_custom_field.id], this.customFieldValues);
        this.getCustomFieldDependencies(this.field, [this.field.child_custom_field.id]);
      } else {
        event.customField.customFieldDependencies = [];
        this.utilService.updateLocalStorage(this.customFieldValuesLSNameKey, this.customFieldValues);
        this.initCheckEndOfProcess();
      }
    } else {
      // On the initial (unchecked) setup, keep any prefilled child value — only a real user
      // un-check should clear the child. Without this guard, a checkbox parent's init wipes a
      // dependency-child field that was prefilled from the URL/SDK (e.g. a comment box that is
      // also a general appointment field), leaving it blank.
      if (!event.isInit) {
        delete this.customFieldValues[this.field.child_custom_field_id];
      }
      event.customField.customFieldDependencies = [];
      this.utilService.updateLocalStorage(this.customFieldValuesLSNameKey, this.customFieldValues);
      this.initCheckEndOfProcess();
    }
  }

  onRadioButtonChangeEvent(event: {
    customField: CustomFieldsDao,
    customFieldOption: CustomFieldOptions,
    oldCustomFieldDependencyIds: number[],
    removeOldCustomFieldDependencyIds: boolean,
  }): void {
    if (event?.oldCustomFieldDependencyIds?.length && event.removeOldCustomFieldDependencyIds) {
      this.customFieldValues = this.removeCustomFieldValuesByIds(event?.oldCustomFieldDependencyIds, this.customFieldValues);
      event.customField.customFieldDependencies = [];
      this.utilService.updateLocalStorage(this.customFieldValuesLSNameKey, this.customFieldValues);
    }

    if (event?.customFieldOption?.dependencies && event?.customFieldOption?.dependencies?.trim() !== '') {
      const dependencies = event.customFieldOption.dependencies.split(',').map(customFieldId => Number(customFieldId));
      this.getCustomFieldDependencies(event.customField, dependencies);
    } else {
      this.initCheckEndOfProcess();
    }

    if (event.customFieldOption.supported_appointment_service_ids) {
      const selectedOptionSupportedServiceIds = JSON.parse(event.customFieldOption.supported_appointment_service_ids) as string[];
      const mappedServiceIdsObj: MappedAppointmentServicesDao = {};
      mappedServiceIdsObj[event.customField.id] = {
        serviceIds: selectedOptionSupportedServiceIds.map(Number),
        is_multiple_select: !!Number(event.customField.is_multiple_select)
      };
      this.appointmentService.mappedServiceIds = mappedServiceIdsObj;
    }
  }

  getCustomFieldDependencies(customFieldData: CustomFieldsDao, cfDependencies: number[]): void {
    this.showDependenciesGhostElement = true;
    customFieldData.customFieldDependencies = [];
    this.formsService.getCustomFieldsByIds(cfDependencies)
      .pipe(finalize(() => this.showDependenciesGhostElement = false))
      .subscribe({
        next: customFields => {
          if (customFields?.length) {
            this.customEventService.childCustomFieldsLoadedEvent.emit(customFields);
            this.updateCustomFieldDependencies(customFields);
            customFieldData.customFieldDependencies = customFields;
          }

          this.initCheckEndOfProcess();
        },
        error: () => this.showDependenciesGhostElement = false
      });
  }

  initCheckEndOfProcess(): void {
    let customFields = new Array<CustomFieldsDao>();
    if (this.customersService.customFieldsBefore?.length) {
      customFields = customFields.concat(this.customersService.customFieldsBefore);
    }
    if (this.customersService.customFieldsAfter?.length) {
      customFields = customFields.concat(this.customersService.customFieldsAfter);
    }
    if (this.customFields?.length) {
      customFields = customFields.concat(this.customFields);
    }
    this.checkEndOfProcessInCustomFields(customFields);
  }

  checkEndOfProcessInCustomFields(customFields: CustomFieldsDao[]): void {
    if (this.recursiveCheckEndOfProcess(customFields)) {
      this.customEventService.hideBookingButtonEvent.emit({
        hide: true
      });
    } else {
      this.customEventService.hideBookingButtonEvent.emit({
        hide: false
      });
    }
  }

  recursiveCheckEndOfProcess(customFields: CustomFieldsDao[]): boolean {
    for (const customField of customFields) {
      if (customField.is_end_of_process === 1) {
        LoggerService.warn('[End of process custom field]', customField);
        return true;
      }

      if (customField?.customFieldDependencies?.length) {
        const hasEndOfProcessInDeps = this.recursiveCheckEndOfProcess(customField.customFieldDependencies);
        if (hasEndOfProcessInDeps) {
          return true;
        }
      }
    }

    return false;
  }

  removeCustomFieldValuesByIds(
    ids: number[],
    customFieldValues: {
      [key: number]: {
        type: string, value: any, option_values: {
          custom_field_option_id: number, custom_field_option_value: string,
        }[]
      } | any
    }
  ): {
    [key: number]: {
      type: string, value: any, option_values: {
        custom_field_option_id: number, custom_field_option_value: string,
      }[]
    } | any
  } {
    if (!customFieldValues) {
      return;
    }

    this.customEventService.removeChildCustomFieldsEvent.emit(ids);
    return customFieldValues;
  }

  updateCustomFieldDependencies(customFieldDependencies: CustomFieldsDao[]): void {
    // Translate any identifier-keyed prefill entries to their numeric custom-field id for the
    // dependency children (which may not be in the parent flow's list at parent-translation time),
    // before the id-keyed prefill loop below reads selectedJsonCustomFields.
    this.selectedJsonCustomFields = this.helperService.resolveCustomFieldIdentifierKeys(this.selectedJsonCustomFields, customFieldDependencies);

    // Add default option if it is select field
    for (const customFieldDependency of customFieldDependencies) {
      if ([
        this.bookingCustomFieldTypes.SELECT,
        this.bookingCustomFieldTypes.CARD_SELECT,
        this.bookingCustomFieldTypes.IMAGE_SELECT
      ].includes(customFieldDependency.type)) {
        if (!this.customFieldValues[customFieldDependency.id]) {
          if (customFieldDependency.is_multiple_select === 1) {
            const temp = this.selectedJsonCustomFields && this.selectedJsonCustomFields[customFieldDependency.id] ? this.selectedJsonCustomFields[customFieldDependency.id] : undefined;
            const finalValue = temp ? temp.split(',').map((value: string) => Number(value)) : [];
            this.customFieldValues[customFieldDependency.id] = { value: finalValue, type: customFieldDependency.type };
          } else {
            if (this.selectedJsonCustomFields && this.selectedJsonCustomFields[customFieldDependency.id]) {
              this.customFieldValues[customFieldDependency.id] = {
                value: Number(this.selectedJsonCustomFields[customFieldDependency.id]),
                type: customFieldDependency.type,
              };
            } else {
              this.customFieldValues[customFieldDependency.id] = {
                value: null,
                type: customFieldDependency.type,
              };
            }
          }
        }
      } else if (customFieldDependency.type === this.bookingCustomFieldTypes.NUMBER_SELECT) {
        const optionIds = customFieldDependency.custom_field_options.map(option => option.id);
        const optionValues = customFieldDependency.custom_field_options.map(option => {
          option.option_value = option.number_default;

          return {
            custom_field_option_id: option.id,
            custom_field_option_value: option.number_default
          };
        });

        this.customFieldValues[customFieldDependency.id] = {
          value: optionIds,
          option_values: optionValues,
          type: customFieldDependency.type
        };
      } else if (customFieldDependency.type === this.bookingCustomFieldTypes.FILE) {
        this.customFieldValues[customFieldDependency.id] = {
          type: this.bookingCustomFieldTypes.FILE,
          file_ids: this.customFieldValues[customFieldDependency.id]?.file_ids?.length > 0 ?
            this.customFieldValues[customFieldDependency.id]?.file_ids : [],
        };
      } else if ([this.bookingCustomFieldTypes.TEXT, this.bookingCustomFieldTypes.TEXTAREA, this.bookingCustomFieldTypes.RADIO, this.bookingCustomFieldTypes.DATE].includes(customFieldDependency.type)) {
        if (this.selectedJsonCustomFields && this.selectedJsonCustomFields[customFieldDependency.id]) {
          this.customFieldValues[customFieldDependency.id] = this.selectedJsonCustomFields[customFieldDependency.id];
        } else {
          this.customFieldValues[customFieldDependency.id] = this.customFieldValues[customFieldDependency.id] ? this.customFieldValues[customFieldDependency.id] : undefined;
        }
      } else if (customFieldDependency.type === this.bookingCustomFieldTypes.CHECKBOX) {
        if (this.selectedJsonCustomFields && this.selectedJsonCustomFields[customFieldDependency.id]) {
          this.customFieldValues[customFieldDependency.id] = ["true", "1", true, 1].includes(this.selectedJsonCustomFields[customFieldDependency.id]) ? true : false;
        } else {
          this.customFieldValues[customFieldDependency.id] = this.customFieldValues[customFieldDependency.id] ? ["true", "1", true, 1].includes(this.customFieldValues[customFieldDependency.id]) ? true : false : false;
        }
      }
    }
  }
}
